import React, { useState, useEffect, useRef, useCallback } from 'react';
import Header, { BreadcrumbItem } from '../components/Header';
import ContentReader from '../components/ContentReader';
import HistoryButton from '../components/HistoryButton';
import StepHelpButton from '../components/StepHelpButton';
import HistoryModal from '../components/HistoryModal';
import { progressService, ProgressItem, JourneyProgress } from '../services/progressService';
import { InventoryService } from '../services/inventoryService';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';
import StepShareDialog from '../components/StepShareDialog';
import { useStepShareDialog } from '../hooks/useStepShareDialog';
import { withShareButton } from '../utils/stepShareUtils';
import { mergeStepActions, stepDataWithActions, buildStepDataWithActions } from '../utils/mergeStepActions';
import { perfMark, perfSummary, perfTimeAsync } from '../utils/perfTimer';
import { getStepActionSchema } from '../config/stepActionSchemas';

interface Step12Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const Step12: React.FC<Step12Props> = ({ user, onLogin, onLogout }) => {
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);

  const [servicePlan, setServicePlan] = useState<string>('');
  const [savingPlan, setSavingPlan] = useState(false);
  const [savedPlan, setSavedPlan] = useState(false);
  const [savePlanError, setSavePlanError] = useState(false);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: "Read the SAA Green Book about Step Twelve",
      description: "See how spiritual awakening naturally leads me to carry the message and live recovery in all areas of life",
      completed: false,
      hasButton: true,
      buttonText: "Read Green Book",
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: "Read guidance on working Step Twelve",
      description: "Read practical guidance on how to work Step Twelve effectively.",
      completed: false,
      hasButton: true,
      buttonText: "Read Guidance",
      buttonAction: () => setIsGuidanceOpen(true)
    },
    {
      id: 3,
      title: "Define my service plan",
      description: "Write out how you want to carry the message and practice these principles in your daily life.",
      completed: false
    },
    {
      id: 4,
      title: "Share and review your work with your sponsor",
      description: "Share your service plan with your sponsor. You can check this off when you've shared or discussed your work — using the share button is optional.",
      completed: false
    },
    {
      id: 5,
      title: "Take Step Twelve",
      description: "Having had a spiritual awakening as the result of these steps, we tried to carry this message to sex addicts and to practice these principles in all our affairs.",
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  const getShareContext = useCallback(
    () => ({ servicePlan }),
    [servicePlan]
  );
  const { openShareDialog, shareDialogProps } = useStepShareDialog(12, user, actions, getShareContext);

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '12 Steps', path: '/saa/12-steps', active: false },
    { label: 'Step Twelve', path: '/saa/12-steps/step-12', active: true }
  ];

  const loadServicePlan = async () => {
    try {
      const inventoryData = await InventoryService.getInventory('step12-service-plan', '1.0');
      if (inventoryData && inventoryData.length > 0) {
        const planData = inventoryData[0];
        if (planData.plan) setServicePlan(planData.plan);
      }
    } catch (err) {
      console.error('Error loading service plan:', err);
    }
  };

  const applyLoadedStepProgress = async (
    existingProgress: ProgressItem | null,
    journeyProgress: JourneyProgress | null
  ) => {
    if (journeyProgress) {
      setIsOptedOut(journeyProgress.opted_out);
    }

    if (existingProgress) {
      const savedData = existingProgress.data || {};
      setStepData(existingProgress);

      if (savedData.actions) {
        const merged = mergeStepActions(12, originalActions, savedData, !!existingProgress.completed);
        setActions(merged.actions);
        if (merged.migrationApplied) {
          await progressService.updateStepProgress({
            program: 'SAA',
            track: '12-steps',
            step_number: 12,
            step_title: 'Step Twelve',
            completed: !!existingProgress.completed,
            data: buildStepDataWithActions(merged.actions, merged.actionSchemaVersion, savedData),
          });
        }
      }
    }
  };

  const refreshStepData = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);

    try {
      perfMark('Step12', 'refreshStepData start');
      const [{ steps, journeyProgress }] = await Promise.all([
        perfTimeAsync('Step12 loadStepPagesProgress (parallel)', () =>
          progressService.loadStepPagesProgress('SAA', '12-steps', [12], { includeJourney: true })
        ),
        loadServicePlan(),
      ]);

      await applyLoadedStepProgress(steps[12] ?? null, journeyProgress);
      clearError();
      perfSummary('Step Twelve page');
    } catch (err: any) {
      console.error('Error loading step progress:', err);
      handleError(err, 'Failed to load your progress. Please try refreshing the page.');
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    refreshStepData();
  }, [user, stepWorkspaceRevision]);

  const handleHistoryStepUpdated = () => {
    setIsHistoryModalOpen(false);
    if (user) {
      void refreshStepData();
    }
  };

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  const saveServicePlan = async () => {
    if (!user) return;

    setSavingPlan(true);
    setSavedPlan(false);
    setSavePlanError(false);

    try {
      await InventoryService.saveInventory('step12-service-plan', '1.0', [{
        plan: servicePlan,
      }]);
      setSavedPlan(true);
      setTimeout(() => setSavedPlan(false), 3000);
    } catch (err) {
      console.error('Error saving service plan:', err);
      setSavePlanError(true);
    } finally {
      setSavingPlan(false);
    }
  };

  const autoSaveServicePlan = () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    autoSaveTimeoutRef.current = setTimeout(() => {
      saveServicePlan();
    }, 2000);
  };

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    if (!user) {
      handleError(new Error('User not logged in'), 'Please log in to save your progress.');
      return;
    }

    try {
      setActions(updatedActions);

      const allCompleted = updatedActions.every(action => action.completed);
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 12,
        step_title: 'Step Twelve',
        completed: allCompleted,
        data: stepDataWithActions(updatedActions, getStepActionSchema(12).actionSchemaVersion)
      });

      if (updatedStepData && updatedStepData.step) {
        setStepData(updatedStepData.step);
      }

      const completedCount = updatedActions.filter(a => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step Twelve - ${completedCount} of 5 actions completed`,
        progress_milestone: allCompleted ? 'step12_completed' : 'step12_in_progress'
      });

      clearError();
    } catch (err: any) {
      console.error('Error updating action progress:', err);
      handleError(err, 'Failed to save progress. Please try again.');
    }
  };

  const renderAction3Content = (action: ActionItem) => {
    if (action.id !== 3) return null;

    return (
      <div className={styles.actionContentIndent}>
        <div style={{ marginBottom: 16, width: '100%' }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{
              fontWeight: 600,
              color: '#333',
              fontSize: '14px',
              display: 'block',
              marginBottom: '8px'
            }}>
              My Plan for Service and Carrying the Message
            </label>
            <textarea
              value={servicePlan}
              onChange={(e) => {
                setServicePlan(e.target.value);
                autoSaveServicePlan();
              }}
              onBlur={saveServicePlan}
              placeholder="Write out your plan for service and carrying the message to others..."
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                border: '1px solid #ccc',
                borderRadius: 4,
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{
            textAlign: 'right',
            minHeight: 24,
            fontSize: '14px',
            fontWeight: 500
          }}>
            {savingPlan && (
              <span style={{ color: '#6c757d' }}>
                Saving...
              </span>
            )}
            {savedPlan && (
              <span style={{ color: '#28a745' }}>
                ✓ All changes saved
              </span>
            )}
            {savePlanError && (
              <span style={{ color: '#dc3545' }}>
                ⚠ Save failed. Please try again.
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const allCompleted = actions.every(action => action.completed);

  return (
    <div className={styles.stepPage}>
      <Header
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />

      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>Step Twelve</h1>
            <StepHelpButton stepKey="app" />
            {stepData && (
              <HistoryButton
                stepNumber={12}
                stepTitle="Step Twelve"
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>
            Having had a spiritual awakening as the result of these steps, we tried to carry this message to sex addicts and to practice these principles in all our affairs.
          </p>
        </div>

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>Refresh Page</button>
              </>
            )}
          </div>
        )}

        {!user && !isInitializing && !errorMessage && (
          <StepLoginPrompt onLogin={onLogin} />
        )}

        {user && isInitializing && !errorMessage && (
          <div className={styles.loadingMessage}>
            <p>Loading your progress...</p>
          </div>
        )}

        {user && !isInitializing && !errorMessage && (
          <div className={styles.actionsContainer}>
            <h2 className={styles.actionsTitle}>Actions to Complete Step Twelve</h2>
            <p className={styles.actionsDescription}>
              Complete each action in order. Check the box when you've finished each action.
            </p>

            <StepActions
              actions={withShareButton(12, actions, openShareDialog)}
              onActionChange={handleActionChange}
              stepNumber={12}
              renderActionContent={renderAction3Content}
            />

            {allCompleted && (
              <div className={styles.completionMessage}>
                <h3>🎉 Congratulations!</h3>
                <p>🎉 Congratulations! You have completed all 12 Steps of SAA. You are now ready to continue your recovery journey and help others.</p>
              </div>
            )}

            {!allCompleted && (
              <div className={styles.stepNavigation}>
                <div className={styles.navigationLinks}>
                  <a href="/saa/12-steps/step-11" className={styles.prevStepLink}>
                    {'<<'} Step Eleven
                  </a>
                  <span></span>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-twelve start>>"
        endTag="<<step-twelve end>>"
      />

      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_12_Guidance.md"
        startTag="<<working-step-twelve start>>"
        endTag="<<working-step-twelve end>>"
        title="Guidance on working Step Twelve"
      />

      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={12}
        stepTitle="Step Twelve"
        stepData={stepData}
        onStepUpdated={handleHistoryStepUpdated}
      />

      <StepShareDialog {...shareDialogProps} />
    </div>
  );
};

export default Step12;
